import React, { useState, useRef } from "react";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { Button, Form, message } from "antd";
import { useNavigate } from "react-router-dom";
import InputCard from "../../../../../Components/Cards/InputCard";
import { useLocation } from "react-router-dom";
import { createFuelBrand } from "../../../../../API/Admin/SystemConfiguration/FuelBrands";
import "./style.css";

const shortenFileName = (fileName, maxLength = 50) => {
  if (!fileName || fileName.length <= maxLength) return fileName;

  const dotIndex = fileName.lastIndexOf(".");
  const extension = dotIndex > 0 ? fileName.slice(dotIndex) : "";
  const baseName = dotIndex > 0 ? fileName.slice(0, dotIndex) : fileName;

  return `${baseName.slice(0, maxLength - extension.length)}${extension}`;
};

const getErrorMessage = (data, fallback) => {
  if (!data) return fallback;
  if (data.message) return data.message;
  if (data.detail) return data.detail;

  const fieldErrors = Object.values(data)
    .flat()
    .filter((msg) => typeof msg === "string");

  return fieldErrors.length ? fieldErrors.join(" ") : fallback;
};

const AddFuelBrand = () => {
  const navigate = useNavigate();
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarError, setAvatarError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  const location = useLocation();
  const [form] = Form.useForm();

  const [formData, setFormData] = useState({
    brandInformation: {},
  });

  const handleBack = () => {
    navigate("/system-configuration", {
      state: { activeTab: location.state?.from || "Fuel Brands" },
    });
  };

  const handleButtonClick = () => fileInputRef.current.click();

  const handleFileChange = (e) => {
    const file = e.target.files[0];

    if (file) {
      setAvatarFile(file);
      setAvatarPreview(URL.createObjectURL(file));
      setAvatarError(false);
    }
  };

  const brandInformationFields = [
    {
      name: "brandName",
      label: "Brand Name",
      type: "text",
      placeholder: "Enter Brand Name",
      rules: [{ required: true, message: "Brand name is required" }],
    },
    {
      name: "legalEntityName",
      label: "Legal Entity Name",
      type: "textarea",
      placeholder: "Enter Legal Entity Name",
      rules: [{ required: true, message: "Legal Entity Name is required" }],
    },
  ];

  const handleSubmit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (error) {
      message.error("Please fill in all required fields.");
      return;
    }

    if (!avatarFile) {
      setAvatarError(true);
      message.error("Please upload brand picture.");
      return;
    }

    const formPayload = new FormData();
    formPayload.append("brand_name", values.brandName);
    formPayload.append("legalentity_name", values.legalEntityName);
    formPayload.append("avatar", avatarFile, shortenFileName(avatarFile.name));

    setSubmitting(true);
    try {
      const res = await createFuelBrand(formPayload);

      if (Math.floor(res?.status / 100) === 2) {
        message.success(res?.data?.message || "Fuel Brand added successfully!");
        navigate("/system-configuration", {
          state: { activeTab: location.state?.from || "Fuel Brands" },
        });
      } else {
        message.error(getErrorMessage(res?.data, "Failed to add fuel brand."));
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to add fuel brand."),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setAvatarFile(null);
    setAvatarPreview(null);
    setAvatarError(false);
    setFormData({ brandInformation: {} });
  };
  return (
    <div className="edit-company add-fuel">
      <div className="back-btn">
        <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
          Back
        </Button>
      </div>
      <div className="title">Add Fuel Brand</div>
      <Form form={form} layout="vertical">
        <div className="row">
          <div className="profile-card">
            <div className="profile-header">
              <div className="btn">
                <Button
                  type="default"
                  className="upload-btn"
                  onClick={handleButtonClick}
                >
                  Upload Photo
                </Button>
              </div>
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleFileChange}
              />

              <div
                className={`profile-avatar ${avatarError ? "avatar-error" : ""}`}
              >
                {avatarPreview ? (
                  <img
                    src={avatarPreview}
                    alt="avatar"
                    style={{
                      width: "100%",
                      height: "100%",
                      borderRadius: "50%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  formData.brandInformation?.brandName
                    ?.charAt(0)
                    .toUpperCase() || "?"
                )}
              </div>

              {avatarError && (
                <div className="error-text">Please upload brand picture</div>
              )}
            </div>
            <div className="profile-body" />
          </div>
        </div>

        <div className="input-cards-container">
          <InputCard
            title="Brand Name"
            fields={brandInformationFields}
            initialData={formData.brandInformation}
            onChange={(data) =>
              setFormData((prev) => ({ ...prev, brandInformation: data }))
            }
          />
        </div>

        <div className="btns-row">
          <Button onClick={handleCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            className="save-btn"
            loading={submitting}
          >
            Add Brand
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default AddFuelBrand;
